import ReactApexChart from 'react-apexcharts'
import type { ApexOptions } from 'apexcharts'

// ── Minimal-UI palette (matches the old dashboard theme) ─────────────────────
export const PALETTE = {
  primary: { lighter: '#fbca9e', light: '#f8ad6a', main: '#f68b2c', dark: '#d87925', darker: '#b1631e' },
  info:    { lighter: '#CAFDF5', light: '#61F3F3', main: '#00B8D9', dark: '#006C9C', darker: '#003768' },
  success: { lighter: '#D3FCD2', light: '#77ED8B', main: '#22C55E', dark: '#118D57', darker: '#065E49' },
  warning: { lighter: '#FFF5CC', light: '#FFD666', main: '#FFAB00', dark: '#B76E00', darker: '#7A4100' },
  error:   { lighter: '#FFE9D5', light: '#FFAC82', main: '#FF5630', dark: '#B71D18', darker: '#7A0916' },
  text:    { primary: '#1C252E', secondary: '#637381', disabled: '#919EAB' },
  divider: 'rgba(145, 158, 171, 0.2)',
  background: { paper: '#FFFFFF', neutral: '#F4F6F8' },
}

const CHART_COLORS = [
  PALETTE.primary.main, PALETTE.warning.main, PALETTE.info.main, PALETTE.error.main,
  PALETTE.success.main, PALETTE.warning.dark, PALETTE.success.darker, PALETTE.info.dark, PALETTE.info.darker,
]

// tiny deep-merge so we don't pull in lodash
function isObj(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}
function merge<T extends Record<string, any>>(base: T, over: Record<string, any>): T {
  const out: Record<string, any> = Array.isArray(base) ? [...(base as any)] : { ...base }
  for (const k of Object.keys(over)) {
    out[k] = isObj(out[k]) && isObj(over[k]) ? merge(out[k], over[k]) : over[k]
  }
  return out as T
}

// ── useChart: faithful port of the old src/components/chart/use-chart.ts ──────
export function useChart(options?: ApexOptions): ApexOptions {
  const LABEL_TOTAL = { show: true, label: 'Total', color: PALETTE.text.secondary, fontSize: '0.875rem', fontWeight: 600, lineHeight: '1.57143' }
  const LABEL_VALUE = { offsetY: 8, color: PALETTE.text.primary, fontSize: '1.5rem', fontWeight: 700, lineHeight: '1.5' }

  const base: ApexOptions = {
    colors: CHART_COLORS,
    chart: { toolbar: { show: false }, zoom: { enabled: false }, foreColor: PALETTE.text.disabled, fontFamily: 'inherit' },
    states: { hover: { filter: { type: 'lighten', value: 0.04 } as any }, active: { filter: { type: 'darken', value: 0.88 } as any } },
    fill: { opacity: 1, gradient: { type: 'vertical', shadeIntensity: 0, opacityFrom: 0.4, opacityTo: 0, stops: [0, 100] } },
    dataLabels: { enabled: false },
    stroke: { width: 3, curve: 'smooth', lineCap: 'round' },
    grid: { strokeDashArray: 3, borderColor: PALETTE.divider, xaxis: { lines: { show: false } } },
    xaxis: { axisBorder: { show: false }, axisTicks: { show: false } },
    markers: { size: 0, strokeColors: PALETTE.background.paper },
    tooltip: { x: { show: true } },
    legend: {
      show: true, fontSize: '15', position: 'top', horizontalAlign: 'right',
      markers: { radius: 12 } as any, fontWeight: 500, itemMargin: { horizontal: 8 },
      labels: { colors: PALETTE.text.primary },
    },
    plotOptions: {
      bar: { borderRadius: 4, columnWidth: '28%', borderRadiusApplication: 'end', borderRadiusWhenStacked: 'last' },
      pie: { donut: { labels: { show: true, value: LABEL_VALUE, total: LABEL_TOTAL } } },
      radialBar: { track: { strokeWidth: '100%' }, dataLabels: { value: LABEL_VALUE as any, total: LABEL_TOTAL as any } },
      radar: { polygons: { fill: { colors: ['transparent'] }, strokeColors: PALETTE.divider, connectorColors: PALETTE.divider } },
      polarArea: { rings: { strokeColor: PALETTE.divider }, spokes: { connectorColors: PALETTE.divider } },
    },
    responsive: [{ breakpoint: 768, options: { plotOptions: { bar: { columnWidth: '40%' } } } }],
  }

  return options ? merge(base, options as Record<string, any>) : base
}

const nf = (v: number | string) => Number(v).toLocaleString()

// ── 1. Payment Methods Revenue — polarArea ───────────────────────────────────
export function PolarPaymentChart({ series, colors }: { series: { label: string; value: number }[]; colors?: string[] }) {
  const options = useChart({
    colors,
    labels: series.map(s => s.label),
    stroke: { colors: [PALETTE.background.paper] },
    fill: { opacity: 0.8 },
    yaxis: { show: false },
    legend: { position: 'right', itemMargin: { horizontal: 10, vertical: 7 } },
    tooltip: { fillSeriesColor: false },
    responsive: [{ breakpoint: 640, options: { legend: { position: 'bottom', horizontalAlign: 'left' } } }],
  })
  return <ReactApexChart type="polarArea" series={series.map(s => s.value)} options={options} height={300} />
}

// ── 2. Trainer / Student Analytics — pie ─────────────────────────────────────
export function PieAnalyticsChart({ series, colors }: { series: { label: string; value: number }[]; colors?: string[] }) {
  const options = useChart({
    chart: { sparkline: { enabled: true } },
    colors,
    labels: series.map(s => s.label),
    stroke: { colors: [PALETTE.background.paper] },
    legend: { floating: true, position: 'bottom', horizontalAlign: 'center' },
    dataLabels: { enabled: true, dropShadow: { enabled: false } },
    tooltip: { fillSeriesColor: false, y: { formatter: (v: number) => nf(v) } },
    plotOptions: { pie: { donut: { labels: { show: false } } } },
  })
  return <ReactApexChart type="pie" series={series.map(s => s.value)} options={options} height={260} />
}

// ── 3. Trainers By Gender — radialBar (gradient) ─────────────────────────────
export function RadialGenderChart({ series, total, colorPairs }: {
  series: { label: string; value: number }[]; total: number; colorPairs?: [string, string][]
}) {
  const pairs = colorPairs ?? [[PALETTE.primary.light, PALETTE.primary.main], [PALETTE.warning.light, PALETTE.warning.main], [PALETTE.info.light, PALETTE.info.main]]
  const options = useChart({
    colors: pairs.map(p => p[1]),
    chart: { sparkline: { enabled: true } },
    labels: series.map(s => s.label),
    legend: { floating: true, position: 'bottom', horizontalAlign: 'center' },
    fill: { type: 'gradient', gradient: { colorStops: pairs.map(p => [{ offset: 0, color: p[0] }, { offset: 100, color: p[1] }]) as any } },
    plotOptions: { radialBar: { hollow: { size: '68%' }, dataLabels: { value: { offsetY: 16 }, total: { show: true, formatter: () => nf(total) } } } },
  })
  return <ReactApexChart type="radialBar" series={series.map(s => s.value)} options={options} height={300} />
}

// ── 4. Enrollment Trends — area ──────────────────────────────────────────────
export function AreaEnrollmentChart({ series, categories, colors }: {
  series: { name: string; data: number[] }[]; categories: string[]; colors?: string[]
}) {
  const options = useChart({
    colors: colors ?? ['#ffab00', '#0da670'],
    legend: { show: false },
    chart: { type: 'area' },
    stroke: { width: 3, curve: 'smooth', lineCap: 'round' },
    fill: { type: 'gradient', gradient: { shadeIntensity: 0, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] } },
    markers: { size: 0, hover: { size: 5 } },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { fontSize: '12px', colors: PALETTE.text.secondary } },
    },
    yaxis: { labels: { formatter: (v: number) => `${Math.round(v)}`, style: { fontSize: '12px', colors: [PALETTE.text.secondary] } } },
    grid: { borderColor: PALETTE.divider, strokeDashArray: 3, xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } } },
    tooltip: { shared: true, intersect: false, y: { formatter: (v: number) => `${nf(v)} students` } },
  })
  return <ReactApexChart type="area" series={series} options={options} height={340} />
}

// ── 5. Student Category Preferences — grouped bar ────────────────────────────
export function BarDemographicsChart({ series, categories, colors }: {
  series: { name: string; data: number[] }[]; categories: string[]; colors?: string[]
}) {
  const options = useChart({
    colors: colors ?? ['#5B8DEE', '#FFA726', '#66BB6A', '#26C6DA', '#AB47BC'],
    chart: { stacked: false, toolbar: { show: false } },
    stroke: { show: true, width: 2, colors: ['transparent'] },
    legend: { show: true, position: 'top', horizontalAlign: 'right', fontSize: '13px', markers: { radius: 4 } as any, itemMargin: { horizontal: 8 } },
    dataLabels: { enabled: false },
    tooltip: { shared: true, intersect: false, y: { formatter: (v: number) => `${nf(v)} students` } },
    xaxis: {
      categories,
      labels: { style: { fontSize: '12px', fontWeight: 500, colors: PALETTE.text.secondary }, hideOverlappingLabels: true, trim: true, rotateAlways: false },
      axisBorder: { show: true, color: PALETTE.divider },
      axisTicks: { show: false },
    },
    yaxis: { labels: { formatter: (v: number) => `${Math.round(v)}`, style: { fontSize: '12px', fontWeight: 500, colors: [PALETTE.text.secondary] } } },
    plotOptions: { bar: { horizontal: false, borderRadius: 6, borderRadiusApplication: 'end', columnWidth: '60%', distributed: false } },
    grid: { show: true, borderColor: PALETTE.divider, strokeDashArray: 3, xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } }, padding: { left: 8, right: 8 } },
  })
  return <ReactApexChart type="bar" series={series} options={options} height={400} />
}

// ── 6. Ranking leaderboard — horizontal bar (e.g. sessions per trainer) ──────
export function BarRankingChart({ categories, data, name, color, unit }: {
  categories: string[]; data: number[]; name?: string; color?: string; unit?: string
}) {
  const options = useChart({
    chart: { type: 'bar' },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, borderRadiusApplication: 'end', barHeight: '55%' } },
    colors: [color ?? PALETTE.primary.main],
    xaxis: { categories },
    dataLabels: { enabled: true, offsetX: 8, style: { fontWeight: 700, colors: [PALETTE.text.primary] } },
    tooltip: { y: { formatter: (v: number) => `${v} ${unit ?? ''}`.trim() } },
    legend: { show: false },
    grid: { xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
  })
  const height = Math.max(220, categories.length * 44)
  return <ReactApexChart type="bar" series={[{ name: name ?? 'Total', data }]} options={options} height={height} />
}

// ── 7. Yearly Revenue — area ─────────────────────────────────────────────────
export function AreaYearlyChart({ series, categories, colors }: {
  series: { name: string; data: number[] }[]; categories: string[]; colors?: string[]
}) {
  const options = useChart({
    colors: colors ?? [PALETTE.primary.main, PALETTE.info.main],
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories },
    // Without this, ApexCharts' default axis-label formatter mirrors the data's
    // own decimal precision (e.g. "3640.94"), so every tick — even round ones —
    // shows a trailing ".00". Round to whole AED for clean tick labels.
    yaxis: { labels: { formatter: (v: number) => nf(Math.round(v)) } },
    tooltip: { y: { formatter: (v: number) => `AED ${nf(v)}` } },
  })
  return <ReactApexChart type="area" series={series} options={options} height={395} />
}

// ── 8. Issued Certificates / Completed Sessions — bar ────────────────────────
export function BarStatisticsChart({ name, data, categories, colors }: {
  name: string; data: number[]; categories: string[]; colors?: string[]
}) {
  const options = useChart({
    colors: colors ?? ['#28a745', '#fd7e14', '#dc3545'],
    stroke: { show: true, width: 2 },
    xaxis: { categories },
    plotOptions: { bar: { horizontal: false, columnWidth: '40%', borderRadius: 4, borderRadiusApplication: 'end' } },
  })
  return <ReactApexChart type="bar" series={[{ name, data }]} options={options} height={400} />
}

// ── 9. Revenue By Package — donut ────────────────────────────────────────────
export function DonutRevenueChart({ series, colors }: { series: { label: string; value: number }[]; colors?: string[] }) {
  const options = useChart({
    chart: { sparkline: { enabled: true } },
    colors,
    labels: series.map(s => s.label),
    stroke: { colors: [PALETTE.background.paper] },
    legend: { offsetY: 0, floating: true, position: 'bottom', horizontalAlign: 'center' },
    tooltip: { fillSeriesColor: false, y: { formatter: (v: number) => nf(v) } },
    plotOptions: { pie: { donut: { size: '90%', labels: { value: { formatter: (v: string | number) => nf(v) }, total: { formatter: (w: any) => nf(w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0)) } } } } },
  })
  return <ReactApexChart type="donut" series={series.map(s => s.value)} options={options} height={300} />
}
